import { useState } from 'react';
import { productsApi, type ExternalProduct, type UnitDef } from '../lib/domain';
import { ApiError } from '../lib/api';

type Current = {
  name: string;
  brand: string | null;
  manufacturer?: string | null;
  netQuantityValue: string | null;
  netQuantityUnit: string | null;
  ingredients?: string | null;
  allergens?: string | null;
};

type FieldKey = 'name' | 'brand' | 'manufacturer' | 'netQuantity' | 'ingredients' | 'allergens';

// "500 g" / "1,5 l" -> Menge + Einheit; alles andere (z. B. "6 x 100 g") lässt sich nicht
// eindeutig übernehmen und wird nur angezeigt.
function parseQuantity(raw: string | undefined, units: UnitDef[]) {
  const m = raw?.match(/^\s*(\d+(?:[.,]\d+)?)\s*([^\d\s]+)\s*$/);
  if (!m) return null;
  const unit = units.find((u) => u.label.toLowerCase() === m[2].toLowerCase() || u.code.toLowerCase() === m[2].toLowerCase());
  return unit ? { value: Number(m[1].replace(',', '.')), unit: unit.code } : null;
}

// Vergleicht die bei OpenFoodFacts gefundenen Daten (je hinterlegter EAN) mit dem Produkt.
// Je Feld entscheidet eine Checkbox, ob der Wert übernommen wird: leere Felder werden
// ergänzt, bereits befüllte Felder ersetzt - und zwar nur, wenn sie angehakt werden.
export function ExternalDataMerge({ productId, current, units, onApplied }: {
  productId: string;
  current: Current;
  units: UnitDef[];
  onApplied: () => void | Promise<void>;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<Array<{ ean: string; external: ExternalProduct }> | null>(null);
  const [selectedEan, setSelectedEan] = useState('');
  const [checked, setChecked] = useState<Partial<Record<FieldKey, boolean>>>({});
  const [applied, setApplied] = useState(false);

  const external = results?.find((r) => r.ean === selectedEan)?.external;

  type Field = { key: FieldKey; label: string; now: string; ext: string; applicable: boolean };
  const fields: Field[] = external
    ? ([
        { key: 'name', label: 'Name', now: current.name, ext: external.name ?? '', applicable: true },
        { key: 'brand', label: 'Marke', now: current.brand ?? '', ext: external.brand ?? '', applicable: true },
        { key: 'manufacturer', label: 'Hersteller', now: current.manufacturer ?? '', ext: external.manufacturer ?? '', applicable: true },
        {
          key: 'netQuantity',
          label: 'Menge',
          now: current.netQuantityValue ? `${Number(current.netQuantityValue)} ${current.netQuantityUnit ?? ''}`.trim() : '',
          ext: external.netQuantity ?? '',
          applicable: !!parseQuantity(external.netQuantity, units),
        },
        { key: 'ingredients', label: 'Zutaten', now: current.ingredients ?? '', ext: external.ingredients ?? '', applicable: true },
        { key: 'allergens', label: 'Allergene', now: current.allergens ?? '', ext: external.allergens ?? '', applicable: true },
      ] as Field[]).filter((f) => f.ext.trim() !== '')
    : [];

  function select(ean: string, list = results) {
    setSelectedEan(ean);
    const ext = list?.find((r) => r.ean === ean)?.external;
    // Vorauswahl: nur leere Felder ergänzen; vorhandene Werte werden nie ungefragt ersetzt.
    setChecked({
      name: !current.name && !!ext?.name,
      brand: !current.brand && !!ext?.brand,
      manufacturer: !current.manufacturer && !!ext?.manufacturer,
      netQuantity: !current.netQuantityValue && !!parseQuantity(ext?.netQuantity, units),
      ingredients: !current.ingredients && !!ext?.ingredients,
      allergens: !current.allergens && !!ext?.allergens,
    });
    setApplied(false);
  }

  async function search() {
    setLoading(true);
    setError(null);
    setApplied(false);
    try {
      const res = await productsApi.externalData(productId);
      const found = res.results.filter((r): r is { ean: string; external: ExternalProduct } => !!r.external);
      setResults(found);
      if (found.length > 0) select(found[0].ean, found);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Suche bei OpenFoodFacts fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  }

  async function apply() {
    const patch: Record<string, unknown> = {};
    for (const f of fields) {
      if (!checked[f.key] || !f.applicable) continue;
      if (f.key === 'netQuantity') {
        const q = parseQuantity(external?.netQuantity, units);
        if (q) {
          patch.netQuantityValue = q.value;
          patch.netQuantityUnit = q.unit;
        }
      } else {
        patch[f.key] = f.ext.trim();
      }
    }
    if (Object.keys(patch).length === 0) return;
    setError(null);
    try {
      await productsApi.update(productId, patch);
      await onApplied();
      setApplied(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Daten konnten nicht übernommen werden.');
    }
  }

  return (
    <div className="stack">
      <div className="row">
        <button onClick={search} disabled={loading}>{loading ? 'Suche…' : 'Bei OpenFoodFacts suchen'}</button>
        <span className="muted" style={{ fontSize: '0.8rem' }}>Es werden alle hinterlegten EANs abgefragt.</span>
      </div>
      {error && <p className="error-text" role="alert">{error}</p>}
      {results && results.length === 0 && <p className="muted">Zu den hinterlegten EANs wurden keine Daten gefunden.</p>}
      {results && results.length > 1 && (
        <div className="field">
          <label htmlFor="off-ean">Treffer für EAN</label>
          <select id="off-ean" value={selectedEan} onChange={(e) => select(e.target.value)}>
            {results.map((r) => (
              <option key={r.ean} value={r.ean}>{r.ean} – {r.external.name ?? 'ohne Namen'}</option>
            ))}
          </select>
        </div>
      )}
      {external && (
        <>
          {fields.map((f) => {
            const same = f.now.trim() === f.ext.trim();
            return (
              <label key={f.key} className="stack" style={{ gap: 2, opacity: same || !f.applicable ? 0.6 : 1 }}>
                <span>
                  <input
                    type="checkbox"
                    checked={!!checked[f.key] && !same && f.applicable}
                    disabled={same || !f.applicable}
                    onChange={(e) => setChecked((c) => ({ ...c, [f.key]: e.target.checked }))}
                  />{' '}
                  <strong>{f.label}</strong>
                  {same && ' (identisch)'}
                  {!f.applicable && ' (Format nicht übernehmbar)'}
                  {!same && f.applicable && (f.now ? ' – ersetzen' : ' – ergänzen')}
                </span>
                {f.now && !same && <span className="muted" style={{ fontSize: '0.85rem' }}>Aktuell: {f.now}</span>}
                <span style={{ fontSize: '0.85rem' }}>OpenFoodFacts: {f.ext}</span>
              </label>
            );
          })}
          {fields.length === 0 && <p className="muted">OpenFoodFacts liefert für diese EAN keine übernehmbaren Felder.</p>}
          <div className="row">
            <button className="primary" onClick={apply} disabled={!fields.some((f) => checked[f.key] && f.applicable && f.now.trim() !== f.ext.trim())}>
              Ausgewählte übernehmen
            </button>
            {applied && <span className="badge ok" role="status">Übernommen</span>}
          </div>
        </>
      )}
    </div>
  );
}
